import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { apiGetSettings } from "../graphQL/settings";
import { DEFAULTS, NEVER_TOGGLE } from "../config/uiToggles";

/**
 * Feature-toggle provider.
 *
 * Resolution (per key): user setting -> domain setting -> DEFAULTS.
 * The backend `settings` query already collapses user -> domain -> defaults
 * when an `authorId` is supplied (findSettings.ts): it returns the user's
 * setting doc if present, else the user's domain doc, else the JSON defaults
 * (none exist for schemaType "uiToggles", so it returns empty). We take that
 * single resolved doc's `data` array of { key, isActive } and layer it over the
 * hardcoded DEFAULTS map, per key.
 *
 * A key resolves ON unless a setting explicitly sets isActive:false.
 * NEVER_TOGGLE keys always resolve ON regardless of config.
 *
 * The resolved map is mirrored to localStorage "cs_ui_toggles" so the very
 * first paint after reload is instant (same pattern as cs_open_panels).
 */

const LS_KEY = "cs_ui_toggles";

const FeatureToggleContext = createContext(null);

const readCache = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") return parsed;
    }
  } catch {
    /* ignore */
  }
  return null;
};

// Pull the { key, isActive } array out of a setting doc's freeform `data`.
// Supports a raw array, { toggles: [...] } (our shape, alongside provenance),
// or the legacy { data: [...] } wrapper used by other settings.
const extractToggleArray = (setting) => {
  const payload = setting?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.toggles)) return payload.toggles;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const pickLatest = (settings) => {
  if (!Array.isArray(settings) || settings.length === 0) return null;
  const ts = (v) => {
    const p = Date.parse(String(v ?? ""));
    return Number.isFinite(p) ? p : Number(v ?? 0) || 0;
  };
  return settings.reduce(
    (a, b) => (ts(b?.updatedAt ?? b?.createdAt) >= ts(a?.updatedAt ?? a?.createdAt) ? b : a),
    settings[0]
  );
};

// Build the resolved { key: boolean } map from a remote toggle array.
const buildResolved = (toggleArray) => {
  const resolved = { ...DEFAULTS };
  (toggleArray || []).forEach((entry) => {
    const key = entry?.key;
    if (!key) return;
    // Only isActive === false hides a control; anything else = ON.
    resolved[key] = entry.isActive !== false;
  });
  return resolved;
};

export const FeatureToggleProvider = ({ children }) => {
  const userId = useSelector((state) => state.auth.userId);
  const domain = useSelector((state) => state.auth.user?.domain || "");

  // Seed from cache (instant first paint), else from hardcoded defaults.
  const [resolved, setResolved] = useState(() => ({ ...DEFAULTS, ...(readCache() || {}) }));

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await apiGetSettings({
          schemaType: "uiToggles",
          type: "app",
          authorId: userId,
        });
        if (cancelled) return;
        const latest = pickLatest(result?.settings);
        const next = buildResolved(extractToggleArray(latest));
        setResolved(next);
        try {
          localStorage.setItem(LS_KEY, JSON.stringify(next));
        } catch {
          /* ignore */
        }
      } catch (e) {
        // Fail open: keep whatever we already resolved (cache or defaults).
        console.error("Failed to load UI feature toggles:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, domain]);

  const isOn = useCallback(
    (key) => {
      if (!key) return true;
      if (NEVER_TOGGLE.has(key)) return true;
      // Registered keys use their resolved value; unknown keys default ON.
      if (Object.prototype.hasOwnProperty.call(resolved, key)) return resolved[key] !== false;
      return true;
    },
    [resolved]
  );

  const value = useMemo(() => ({ resolved, isOn }), [resolved, isOn]);

  return <FeatureToggleContext.Provider value={value}>{children}</FeatureToggleContext.Provider>;
};

const useFeatureToggleContext = () => useContext(FeatureToggleContext);

/**
 * useToggle(key) => boolean. Safe to call even outside the provider
 * (returns true so controls fail open) — but the app wraps everything.
 */
export const useToggle = (key) => {
  const ctx = useFeatureToggleContext();
  if (!ctx) return true;
  return ctx.isOn(key);
};

/** useToggles() => whole resolved map + isOn helper. */
export const useToggles = () => {
  const ctx = useFeatureToggleContext();
  if (!ctx) return { resolved: { ...DEFAULTS }, isOn: () => true };
  return ctx;
};

export default FeatureToggleProvider;
